import { getRuntimeEnv } from "../config/runtimeEnv";
import { sessionId } from "../config/session";

/**
 * Encaminha os `console.*` do shell e do softphone embarcado para o backend, que os grava num
 * arquivo por ramal. Existe porque no Teams do Android e do iPhone não há DevTools: o WebView
 * do app de produção não é inspecionável, então sem isto uma falha no celular é invisível.
 *
 * O gate é o `sipConfig.enableDebug` do usuário, administrado no msteams-ui e entregue no
 * check-in — o mesmo campo que já vira `logger=` na URL do softphone.
 *
 * Ordenação é requisito, não detalhe: o arquivo mistura shell e softphone justamente para que o
 * intercalamento cronológico conte a história. Por isso há **uma única requisição em voo por
 * vez** — POSTs concorrentes chegavam trocados e o servidor grava na ordem de chegada. Enquanto
 * uma requisição não resolve, as linhas se acumulam aqui e saem juntas na seguinte, então o lote
 * é o que a rede deixou acumular: com rede boa e log esparso, uma linha por POST, como antes.
 */

export type ClientLogSource = "shell" | "softphone";

export interface ClientLogEntry {
  ts: number;
  seq: number;
  level: string;
  message: string;
  source: ClientLogSource;
}

type Sender = (event: { type: string; payload?: unknown }, signal?: AbortSignal) => Promise<void>;

const LEVELS = ["log", "info", "warn", "error", "debug"] as const;

/**
 * Teto do buffer **e** do POST, em bytes — o buffer inteiro sempre cabe numa requisição, então
 * o flush nunca precisa fatiar. Em bytes e não em contagem de linhas porque a restrição real é o
 * corpo HTTP: 200 linhas podem ser 20 KB do shell ou 400 KB de mensagens SIP.
 *
 * ATENÇÃO: este default é duplicado em `msteams-service` (`app-service.js`, de onde sai o limite
 * do body parser da rota). Os dois têm de andar juntos — um cliente montando lotes maiores do que
 * a rota aceita levaria 413 sistemático, visível só sob carga.
 */
const DEFAULT_MAX_BATCH_BYTES = 512 * 1024;
// Rede de segurança: toda linha enfileirada que não pôde sair na hora agenda uma nova tentativa.
const FLUSH_INTERVAL_MS = 2000;
// Sem isto, uma requisição pendurada travaria a cadeia para sempre e o buffer encheria até
// descartar tudo. Com uma requisição em voo, o timeout é o que garante que a fila volte a andar.
const SEND_TIMEOUT_MS = 20 * 1000;
const MAX_MESSAGE_LENGTH = 4 * 1024;
// Reservado no teto para que os marcadores sempre caibam: overflow, despejo de `debug` e falha de
// envio — três, no pior caso, e nenhum deles pode ser a linha que não coube.
const MARKER_RESERVE_BYTES = 512;
// Depois de uma falha, segura os envios por um tempo. Sem isto, um endpoint em erro seria
// martelado a cada linha nova — e o log de diagnóstico viraria a causa do próximo incidente.
const FAILURE_BACKOFF_MS = 5 * 1000;
// `ts`, `seq`, `level`, `source` e a pontuação JSON de uma entrada.
const ENTRY_OVERHEAD_BYTES = 80;

const resolveMaxBatchBytes = (): number => {
  const raw = Number(getRuntimeEnv("REACT_APP_CLIENT_LOG_MAX_BATCH_BYTES"));
  // Zero ou lixo cai no default: um buffer de tamanho zero descartaria tudo em silêncio.
  return Number.isFinite(raw) && raw > MARKER_RESERVE_BYTES ? Math.floor(raw) : DEFAULT_MAX_BATCH_BYTES;
};

// Mesmo id que vai em toda requisição autenticada (src/config/session.ts): o arquivo de log
// e o registro de sessões do backend falam da mesma instância.

const buffer: ClientLogEntry[] = [];
const restoreConsole: Array<() => void> = [];

let sender: Sender | null = null;
let extension = "";
let enabled: boolean | null = null; // null enquanto o check-in não decidiu
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight = false;
let seqCounter = 0;
let bufferBytes = 0;
let sessionStarted = false;

// Linhas recusadas por buffer cheio, e o ponto onde a lacuna começou.
let droppedCount = 0;
let droppedTs = 0;
let droppedSeq = 0;
// Entradas `debug` despejadas do buffer para abrir espaço a uma linha protegida (ver `enqueue`).
let evictedCount = 0;
let evictedTs = 0;
let evictedSeq = 0;
// Linhas perdidas por falha/timeout de envio.
let failedCount = 0;
let failureBackoffUntil = 0;

const truncate = (value: string) =>
  value.length > MAX_MESSAGE_LENGTH ? `${value.slice(0, MAX_MESSAGE_LENGTH)}…[truncado]` : value;

/**
 * Comprimento em bytes UTF-8 sem alocar. Um `TextEncoder.encode()` por `console.*` geraria um
 * `Uint8Array` descartável a cada linha — sob trace SIP isso é pressão de GC à toa.
 */
const utf8Length = (value: string): number => {
  let bytes = 0;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4; // par surrogate: conta os dois de uma vez
      i += 1;
    } else {
      bytes += 3;
    }
  }
  return bytes;
};

const entryBytes = (entry: ClientLogEntry) => utf8Length(entry.message) + ENTRY_OVERHEAD_BYTES;

// Abaixo disto não cabe nem uma estrutura vazia com marcador; devolve só as reticências.
const MIN_BUDGET = 12;
// Folga para o marcador de omissão (`,"…":12`) caber sempre.
const MARKER_RESERVE = 16;
const ELLIPSIS = "…";

/**
 * Serializa em JSON dentro de um orçamento de bytes **preservando a estrutura**: strings são
 * encurtadas, caudas de array e campos de objeto são omitidos, e chaves e colchetes são sempre
 * fechados. O que chega é sempre parseável; o que falta é contado no marcador `"…": <n>` (objetos)
 * ou `"…+<n>"` (arrays).
 *
 * Existe porque o corte cego anterior — `slice()` na mensagem já montada — produzia JSON inválido
 * exatamente nas linhas maiores, que são as que mais têm a dizer.
 *
 * ATENÇÃO: duplicado em `msteams-softphone/src/console-capture.js`, que é injetado no realm do
 * iframe e não pode importar daqui. Os dois têm de andar juntos.
 */
function safeStringify(value: unknown, budget: number): string {
  if (budget < MIN_BUDGET) return `"${ELLIPSIS}"`;

  let whole: string | undefined;
  try {
    whole = JSON.stringify(value);
  } catch {
    // Referência circular, `BigInt`, `toJSON` que lança.
    return '"[nao serializavel]"';
  }
  if (whole === undefined) return "null";
  if (whole.length <= budget) return whole;

  if (typeof value === "string") {
    let slice = value;
    // O escape do JSON torna o tamanho final imprevisível: converge por tentativa.
    while (slice.length > 0 && JSON.stringify(slice + ELLIPSIS).length > budget) {
      slice = slice.slice(0, Math.floor(slice.length * 0.9));
    }
    return JSON.stringify(slice + ELLIPSIS);
  }

  if (typeof value !== "object" || value === null) return `"${ELLIPSIS}"`;

  const isArray = Array.isArray(value);
  const keys = isArray ? null : Object.keys(value as Record<string, unknown>);
  const total = isArray ? (value as unknown[]).length : (keys as string[]).length;
  const parts: string[] = [];
  let used = 2; // os delimitadores

  for (let i = 0; i < total; i += 1) {
    const item = isArray ? (value as unknown[])[i] : (value as Record<string, unknown>)[(keys as string[])[i]];
    // O JSON.stringify também os omitiria.
    if (typeof item === "undefined" || typeof item === "function") continue;

    const prefix = isArray ? "" : `${JSON.stringify((keys as string[])[i])}:`;
    const sep = parts.length ? 1 : 0;
    const room = budget - used - sep - prefix.length - MARKER_RESERVE;
    if (room < MIN_BUDGET) break;

    const piece = safeStringify(item, room);
    if (used + sep + prefix.length + piece.length > budget - MARKER_RESERVE) break;
    used += sep + prefix.length + piece.length;
    parts.push(prefix + piece);
  }

  const omitted = total - parts.length;
  if (omitted > 0) {
    parts.push(isArray ? JSON.stringify(`${ELLIPSIS}+${omitted}`) : `${JSON.stringify(ELLIPSIS)}:${omitted}`);
  }
  return (isArray ? "[" : "{") + parts.join(",") + (isArray ? "]" : "}");
}

const formatArgs = (args: unknown[]): string => {
  const parts: string[] = [];
  let used = 0;
  args.forEach((arg) => {
    let piece: string;
    if (typeof arg === "string") {
      piece = arg;
    } else if (arg instanceof Error) {
      piece = `${arg.name}: ${arg.message}`;
    } else {
      // Orçamento é o que sobrou da mensagem, para que o `truncate` final nunca alcance um trecho
      // JSON — é isso que garante que a estrutura chegue íntegra.
      piece = safeStringify(arg, MAX_MESSAGE_LENGTH - used - 1);
    }
    parts.push(piece);
    used += piece.length + 1;
  });
  return parts.join(" ");
};

/**
 * Envia com timeout. O `signal` vai para o `fetch` do transporte, então o abort cancela a
 * requisição de verdade; a corrida com o timer garante que a cadeia ande mesmo que o transporte
 * ignore o sinal ou pendure antes do fetch (o SSO é resolvido lá dentro).
 */
function sendWithTimeout(payload: unknown): Promise<void> {
  const controller = new AbortController();
  const active = sender;
  if (!active) return Promise.reject(new Error("sender ausente"));

  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error("timeout"));
    }, SEND_TIMEOUT_MS);
  });

  return Promise.race([active({ type: "console", payload }, controller.signal), timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  }) as Promise<void>;
}

/** Marcador de perda, anexado ao lote. Nunca passa pelo `console` — viraria outra linha. */
function buildMarker(message: string, ts: number, seq: number): ClientLogEntry {
  return { source: "shell", level: "warn", message, ts, seq };
}

function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  // Uma de cada vez: é isto que preserva a ordem. O `finally` abaixo redispara com o acumulado.
  if (!enabled || !sender || inFlight) return;

  const wait = failureBackoffUntil - Date.now();
  if (wait > 0) {
    flushTimer = setTimeout(flush, wait);
    return;
  }

  // Um lote só sai se houver linha de verdade. Os marcadores de perda pegam carona no próximo
  // lote — nunca disparam um envio sozinhos. Isto é o que impede o laço: um envio que falha
  // gera um marcador, e se o marcador pudesse disparar outro envio, a falha se realimentaria.
  if (buffer.length === 0) return;

  const entries = buffer.splice(0, buffer.length);
  bufferBytes = 0;

  if (droppedCount > 0) {
    // `ts`/`seq` do primeiro descarte: o marcador cai onde a lacuna começa, não no fim do lote.
    entries.push(buildMarker(`buffer overflow: ${droppedCount} linha(s) descartada(s)`, droppedTs, droppedSeq));
    droppedCount = 0;
  }
  if (evictedCount > 0) {
    // Separado do anterior de propósito: "recusei a linha nova" e "joguei fora linhas de debug que
    // já estavam aqui" são perdas de naturezas diferentes, e confundi-las esconderia qual das duas
    // pressões o buffer sofreu.
    entries.push(buildMarker(`buffer cheio: ${evictedCount} linha(s) debug despejada(s)`, evictedTs, evictedSeq));
    evictedCount = 0;
  }
  if (failedCount > 0) {
    entries.push(buildMarker(`envio falhou: ${failedCount} linha(s) perdida(s)`, Date.now(), (seqCounter += 1)));
    failedCount = 0;
  }
  if (entries.length === 0) return;

  inFlight = true;
  // `tzOffset` para o servidor imprimir a hora do **aparelho**, que é o que o cabeçalho do
  // arquivo promete. Sem ele, o `ts` (epoch) sairia formatado no fuso do host do serviço.
  sendWithTimeout({ extension, sessionId, tzOffset: new Date().getTimezoneOffset(), entries })
    .catch(() => {
      // Falha nunca vai para o `console`: geraria outra linha, outro POST, outra falha. Vira
      // contador, que sai como uma única entrada no lote seguinte.
      failedCount += entries.length;
      failureBackoffUntil = Date.now() + FAILURE_BACKOFF_MS;
    })
    .finally(() => {
      inFlight = false;
      // Só linha nova redispara. Contador de perda não é gatilho — ver o comentário no início
      // do flush. E vai por `scheduleFlush`, que respeita o backoff em vez de girar em vazio.
      if (buffer.length > 0) scheduleFlush();
    });
}

function scheduleFlush() {
  if (!inFlight) {
    flush();
    return;
  }
  // Em voo: as linhas ficam no buffer e o `finally` as leva. O timer é só rede de segurança.
  if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
}

/**
 * Abre espaço para uma linha protegida despejando as entradas `debug` mais antigas do buffer.
 *
 * Existe porque o diagnóstico de mídia do softphone (`[rtc.*]`, ver `webrtc-capture.js`) é um
 * segundo produtor de alto volume: sem isto, uma rajada dele poderia empurrar para fora um veredito
 * ou uma mensagem SIP. Em operação normal o buffer nem chega ao teto — isto é rede de segurança
 * para o caso patológico, com a rede travada durante uma rajada de estabelecimento.
 *
 * Despeja pela cabeça: entre linhas igualmente sacrificáveis, a mais antiga é a menos útil.
 */
function evictDebug(needed: number, ceiling: number) {
  for (let i = 0; i < buffer.length && bufferBytes + needed > ceiling; ) {
    if (buffer[i].level !== "debug") {
      i += 1;
      continue;
    }
    const [victim] = buffer.splice(i, 1); // sem avançar `i`: o próximo já ocupou esta posição
    bufferBytes -= entryBytes(victim);
    if (evictedCount === 0) {
      evictedTs = victim.ts;
      evictedSeq = victim.seq;
    }
    evictedCount += 1;
  }
}

/**
 * Enfileira uma linha. Antes do check-in acumula; depois, envia ou descarta conforme o gate.
 *
 * `ts` é o instante do `console.*` na origem — o do softphone vem de dentro do iframe. Sem ele,
 * a hora gravada seria a da entrega do postMessage, não a do evento.
 */
export function enqueue(source: ClientLogSource, level: string, message: string, ts?: number) {
  if (enabled === false) return;

  const entry: ClientLogEntry = {
    source,
    level,
    message: truncate(message),
    ts: Number.isFinite(ts) ? (ts as number) : Date.now(),
    seq: (seqCounter += 1),
  };

  const size = entryBytes(entry);
  const ceiling = resolveMaxBatchBytes() - MARKER_RESERVE_BYTES;

  if (bufferBytes + size > ceiling) {
    // Cheio. Duas faixas, não uma ordem de severidade: `debug` é sacrificável, e `log`, `info`,
    // `warn` e `error` são protegidos **sem hierarquia entre si**. O trace SIP inteiro sai em
    // nível `log` e é ele que explica qualquer `error`; ordenar por severidade aqui preservaria o
    // sintoma e destruiria a narrativa que o justifica.
    if (entry.level !== "debug") {
      evictDebug(size, ceiling);
    }

    if (bufferBytes + size > ceiling) {
      // Sem `debug` para despejar: volta à política de sempre, descartar as **novas** e não as
      // antigas. O boot e o registro SIP são justamente o que precisa ser visto num celular, e são
      // o que uma fila que descarta pela cabeça perderia. A lacuna vira o marcador do flush; o
      // salto em `seq` a torna visível mesmo sem ele.
      if (droppedCount === 0) {
        droppedTs = entry.ts;
        droppedSeq = entry.seq;
      }
      droppedCount += 1;
      return;
    }
  }

  buffer.push(entry);
  bufferBytes += size;
  if (enabled) scheduleFlush();
}

/**
 * Embrulha o `console` para capturar as linhas desta origem. Chamado no boot, antes de o
 * check-in decidir — é o que permite ver o que aconteceu antes do SSO no celular. Se o gate
 * vier desligado, `setContext` desfaz o patch.
 */
export function captureConsole(source: ClientLogSource) {
  if (enabled === false) return;

  // Antes de qualquer captura, para que a identificação da sessão seja a primeira linha do
  // arquivo em vez de aparecer no meio, depois de todo o backlog do boot.
  if (!sessionStarted) {
    sessionStarted = true;
    enqueue("shell", "info", `sessionStart session=${sessionId} platform=${navigator.platform} ua=${navigator.userAgent}`);
  }

  LEVELS.forEach((level) => {
    const original = console[level];
    console[level] = (...args: unknown[]) => {
      try {
        enqueue(source, level, formatArgs(args));
      } catch {
        /* nunca deixar a captura quebrar quem chamou o console */
      }
      original.apply(console, args as never);
    };
    restoreConsole.push(() => {
      console[level] = original;
    });
  });

  window.addEventListener("error", (event) =>
    enqueue(source, "error", `${event.message} @ ${event.filename}:${event.lineno}`)
  );
  window.addEventListener("unhandledrejection", (event) =>
    enqueue(source, "error", `unhandledrejection: ${formatArgs([event.reason])}`)
  );
}

/** Registra o transporte autenticado (o `sendAppEventNotification` do bootstrap). */
export function setSender(next: Sender) {
  sender = next;
  if (enabled) flush();
}

/**
 * Aplica a decisão do check-in. Ligado, drena a fila acumulada no boot; desligado, descarta a
 * fila e devolve o `console` ao original, zerando o custo para quem não está depurando.
 */
export function setContext(context: { extension?: string; enabled: boolean }) {
  extension = context.extension || "";
  enabled = context.enabled;

  if (!context.enabled) {
    buffer.length = 0;
    bufferBytes = 0;
    droppedCount = 0;
    evictedCount = 0;
    failedCount = 0;
    restoreConsole.splice(0, restoreConsole.length).forEach((restore) => restore());
    return;
  }

  flush();
}
